import React from "react";
import { SnmpDiagnosis } from "../types";

/**
 * Why an SNMP poll failed, in terms a technician can act on.
 *
 * The relay reports precisely what went wrong rather than a blanket timeout,
 * and on its own that is not much use: "Unknown USM user" and "Wrong SNMP PDU
 * digest" are protocol jargon. This renders the three things somebody
 * actually needs — what it means, what to do, and WHICH END IS AT FAULT.
 *
 * The last one earns its place at the top. An afternoon went into a fleet
 * that would not poll, checking the vendor dashboard over and over, because
 * the product said "No SNMP response received before timeout" when the fault
 * was in our own relay. A message that does not name the end sends people to
 * the wrong one.
 *
 * THE RAW ERROR IS ALWAYS STILL SHOWN BY THE CALLER. This sits beside it,
 * never in place of it — an unrecognised failure produces no diagnosis at
 * all, and that is exactly when the raw string matters most.
 */

const SIDE: Record<
  string,
  { label: string; chip: string; ring: string }
> = {
  ours: {
    label: "Our end",
    // Amber, not red: it is a configuration gap on our side, and it is the
    // one the operator can most likely fix immediately.
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
    ring: "border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/10",
  },
  device: {
    label: "The device",
    chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    ring: "border-sky-200 dark:border-sky-800/60 bg-sky-50/60 dark:bg-sky-900/10",
  },
  network: {
    label: "Network / unreachable",
    chip: "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200",
    ring: "border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30",
  },
  unknown: {
    label: "Unclear",
    chip: "bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-200",
    ring: "border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30",
  },
};

/** Compact single-line form for the hover panel, where space is tight. */
export function SnmpDiagnosisBrief({ d }: { d: SnmpDiagnosis }) {
  const s = SIDE[d.side] || SIDE.unknown;
  return (
    <div className={`mt-1.5 rounded-md border p-2 ${s.ring}`}>
      <div className="flex items-center gap-1.5">
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.chip}`}>
          {s.label}
        </span>
        <span className="font-semibold text-[11px] text-gray-900 dark:text-gray-100">
          {d.title}
        </span>
      </div>
      <div className="mt-1 text-[11px] text-gray-600 dark:text-gray-300 leading-snug">
        {d.fix}
      </div>
    </div>
  );
}

/** Full form for the detail drawer. */
export default function SnmpDiagnosisPanel({ d }: { d: SnmpDiagnosis }) {
  const s = SIDE[d.side] || SIDE.unknown;
  return (
    <div className={`rounded-lg border p-3 ${s.ring}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.chip}`}>
          {s.label}
        </span>
        <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
          {d.title}
        </span>
        {d.fixable_here && (
          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            Fixable here
          </span>
        )}
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            What it means
          </div>
          <div className="mt-0.5 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
            {d.means}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
            What to do
          </div>
          <div className="mt-0.5 text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
            {d.fix}
          </div>
        </div>
      </div>
    </div>
  );
}
